/**
 * AUTOMATION API — wrapper dei REST Liferay allineato a `AutomationsResourceImpl.java`.
 *
 * Contratto (rest-openapi.yaml, tag Automations, base `/o/crm/v1_0`):
 *   - GET    /automations/{groupId}            -> Page<AutomationDefinition>
 *     Query: page, pageSize, sort, search, template (bool), status (int32).
 *   - POST   /automations/{groupId}            -> AutomationDefinition (albero)
 *   - GET    /automations/categories/{groupId} -> Page<AutomationCategory>
 *   - GET    /automation/{id}                  -> AutomationDefinition
 *   - PATCH  /automation/{id}                  -> AutomationDefinition
 *   - DELETE /automation/{id}                  -> 200
 *   - POST   /automation/{id}/publish|suspend|resume|new-version -> AutomationDefinition
 *   - GET    /automation/{id}/stats            -> AutomationStats
 *
 * NB: path singolare per single-entity, plurale per list/create/categories.
 * `status` viaggia int32: la conversione string<->int e' in automationMappers,
 * applicata ai boundary qui (`automationFromDto` sulle response tipizzate).
 * Le transizioni sono POST senza body.
 */

import type { Page, ServerListParams } from '@crm/shared';
import { httpClient } from '../../api/httpClient';
import { createCrudApi } from '../../store/createCrudApi';
import {
  automationExecutionFromDto,
  automationFromDto,
  automationRunFromDto,
  type AutomationDto,
  type AutomationExecutionDto,
  type AutomationRunDto,
} from './automationMappers';
import type {
  Automation,
  AutomationActionType,
  AutomationCategory,
  AutomationCreatePayload,
  AutomationExecution,
  AutomationObjectType,
  AutomationRun,
  AutomationSimulation,
  AutomationStats,
  AutomationUpdatePayload,
  AutomationViolation,
  CatalogField,
} from './automationTypes';

const COLLECTION = '/automations';
const SINGLE = '/automation';

export interface AutomationListParams extends ServerListParams {
  /** true = solo template, false = solo automazioni operative; omesso = entrambe. */
  template?: boolean;
  /** Id enum AutomationStatus; omesso = tutte le non archiviate. */
  status?: number;
}

/**
 * I 5 CRUD standard vengono da `createCrudApi`. Automation non ha mapper
 * create/update dedicati: il payload di create è il body POST così com'è; per
 * l'update si toglie solo `automationDefinitionId` (che vive nell'URL del PATCH).
 */
const base = createCrudApi<
  Automation,
  AutomationDto,
  AutomationCreatePayload,
  AutomationUpdatePayload,
  AutomationListParams
>({
  collection: COLLECTION,
  single: SINGLE,
  fromDto: automationFromDto,
  createToInput: (payload) => payload,
  updateId: (payload) => payload.automationDefinitionId,
  updateToInput: (payload) => {
    const { automationDefinitionId: _id, ...body } = payload;
    return body;
  },
});

export const automationApi = {
  ...base,

  /**
   * Valida una bozza in composizione (POST /automations/{groupId}/validate): elenca i problemi che
   * impedirebbero di PUBBLICARLA. Non persiste nulla e risponde sempre 200 — lista vuota =
   * pubblicabile. Stesso payload della create (i campi mancanti sono la norma: in composizione
   * l'automazione è incompleta) e stesso envelope Page delle altre list.
   * Le regole vivono nel backend: qui non si valida nulla, si chiede.
   */
  validate: (groupId: number, payload: AutomationCreatePayload): Promise<AutomationViolation[]> =>
    httpClient
      .post<Page<AutomationViolation>>(`${COLLECTION}/${groupId}/validate`, payload)
      .then((r) => r.data.items),

  /** Categorie del sito (read-only, per il dropdown del builder). */
  getCategories: (groupId: number): Promise<AutomationCategory[]> =>
    httpClient
      .get<Page<AutomationCategory>>(`${COLLECTION}/categories/${groupId}`)
      .then((r) => r.data.items),

  /**
   * Catalogo QUANDO (read-only, per-sito): oggetti automatizzabili + eventi + parametri, filtrati
   * per il sito (un oggetto è incluso solo se abilitato per il groupId, es. entità di un plugin
   * cliente attivo). La response è l'envelope Page come `getCategories` (il REST Builder serializza
   * `Page.of(...)` come `{items:[...]}` anche se l'OpenAPI dichiara `type: array`), quindi si legge
   * `r.data.items`. Nessuna conversione: i tipi mappano 1:1 il DTO.
   */
  getObjectTypes: (groupId: number): Promise<AutomationObjectType[]> =>
    httpClient
      .get<Page<AutomationObjectType>>(`${COLLECTION}/object-types/${groupId}`)
      .then((r) => r.data.items),

  /**
   * Catalogo SE (read-only, per-sito): i campi filtrabili dell'entità scelta nel QUANDO
   * (`className` FQN), ciascuno con tipo-valore, operatori ed eventuali `enumValues`. Stesso
   * envelope Page di `getObjectTypes` (REST Builder serializza `Page.of(...)` come `{items:[...]}`).
   */
  getConditionTypes: (groupId: number, className: string): Promise<CatalogField[]> =>
    httpClient
      .get<Page<CatalogField>>(`${COLLECTION}/condition-types/${groupId}`, {
        params: { className },
      })
      .then((r) => r.data.items),

  /**
   * Catalogo dei campi SCRIVIBILI (read-only, per-sito): i campi che un'automazione può assegnare
   * sull'entità scelta nel QUANDO, ciascuno con tipo-valore ed eventuali `enumValues`. Stesso
   * schema `CatalogField` del catalogo SE, **senza `operators`**: servono a confrontare, e qui si
   * assegna. Elenco separato da quello dei filtrabili, non un suo sottoinsieme.
   */
  getUpdatableFields: (groupId: number, className: string): Promise<CatalogField[]> =>
    httpClient
      .get<Page<CatalogField>>(`${COLLECTION}/updatable-fields/${groupId}`, {
        params: { className },
      })
      .then((r) => r.data.items),

  /**
   * Catalogo ALLORA (read-only): le azioni eseguibili registrate, ciascuna con `code`, `label`
   * localizzata e `icon` (token da risolvere client-side). Stesso envelope Page degli altri due
   * cataloghi, e come loro dipende dall'entità: `className` è OBBLIGATORIO e il server restituisce
   * le sole azioni applicabili a quell'oggetto — aggiornare un campo non ha senso dove l'entità non
   * ne dichiara di scrivibili. Chi decide è l'azione lato server, non il client.
   */
  getActionTypes: (groupId: number, className: string): Promise<AutomationActionType[]> =>
    httpClient
      .get<Page<AutomationActionType>>(`${COLLECTION}/action-types/${groupId}`, {
        params: { className },
      })
      .then((r) => r.data.items),

  /** Statistiche di esecuzione di una singola automazione (colonne dell'elenco + monitoraggio). */
  getStats: (
    automationDefinitionId: number,
    params?: { startDateFrom?: string; startDateTo?: string },
  ): Promise<AutomationStats> =>
    httpClient
      .get<AutomationStats>(`${SINGLE}/${automationDefinitionId}/stats`, {
        params: params as Record<string, unknown> | undefined,
      })
      .then((r) => r.data),

  /** Log dei run di un'automazione (paginato, più recenti in cima). Monitoraggio. */
  getRuns: (
    automationDefinitionId: number,
    params?: {
      page?: number;
      pageSize?: number;
      sort?: string;
      startDateFrom?: string;
      startDateTo?: string;
    },
  ): Promise<Page<AutomationRun>> =>
    httpClient
      .get<Page<AutomationRunDto>>(`${SINGLE}/${automationDefinitionId}/runs`, {
        params: params as Record<string, unknown> | undefined,
      })
      .then((r) => ({ ...r.data, items: r.data.items.map(automationRunFromDto) })),

  /**
   * Cancella un run (DELETE /automation/{id}/runs/{runId}) con tutto il suo sottoalbero: esiti per
   * record, azioni tentate e lavoro eventualmente ancora in coda. La cascata la fa il server.
   * Non restituisce nulla — ciò che è sparito il client lo sa già, e la tabella toglie la riga da
   * sé senza rifare la fetch.
   * Il server rifiuta con 400 un run ancora VIVO; un run appeso da più della soglia di abbandono
   * si cancella, ed è il caso in cui la pulizia serve di più.
   */
  deleteRun: (automationDefinitionId: number, automationRunId: number): Promise<void> =>
    httpClient
      .delete(`${SINGLE}/${automationDefinitionId}/runs/${automationRunId}`)
      .then(() => undefined),

  /**
   * Ripulisce lo storico dalle passate senza esiti né lavoro in coda (DELETE
   * /automation/{id}/runs): lo scatto
   * soppresso dall'idempotenza, la passata senza record idonei, quella senza candidati. Dopo la
   * pulizia di fine passata i tre casi sono indistinguibili, e si cancellano insieme.
   * Non restituisce nulla, e a differenza della cancellazione singola non c'è nessun 400: le
   * passate ancora vive il server le salta invece di rifiutare. Quante ne siano sparite il client
   * non lo sa — non conosce quali righe avessero esiti — quindi qui il rifetch del log è
   * obbligato, e non un giro di rete evitabile come nel caso singolo.
   */
  deleteEmptyRuns: (automationDefinitionId: number): Promise<void> =>
    httpClient.delete(`${SINGLE}/${automationDefinitionId}/runs`).then(() => undefined),

  /**
   * Esiti per record di un run (GET /automation/{id}/runs/{runId}/executions): il drill-down della
   * riga del log. NON paginato — la pagina contiene tutti gli esiti di quel run — perché il
   * dettaglio si apre su un run alla volta e lo si guarda per intero.
   */
  getRunExecutions: (
    automationDefinitionId: number,
    automationRunId: number,
  ): Promise<Page<AutomationExecution>> =>
    httpClient
      .get<Page<AutomationExecutionDto>>(
        `${SINGLE}/${automationDefinitionId}/runs/${automationRunId}/executions`,
      )
      .then((r) => ({ ...r.data, items: r.data.items.map(automationExecutionFromDto) })),

  /**
   * Valida l'automazione SALVATA (GET /automation/{id}/validation): gli stessi problemi che
   * `validate` qui sopra elenca per una bozza in composizione, ma giudicati sul RECORD IN DB.
   * Le due non sono intercambiabili, ed è il motivo per cui esistono entrambe: `validate` prende un
   * payload, e chi ce l'ha è il builder, che sta per salvarlo; questa prende un id, e chi ha solo
   * quello — la pagina di simulazione — dovrebbe altrimenti ricostruire il payload dal record,
   * cioè reinventare la serializzazione del builder e ottenere il verdetto su ciò che il CLIENT
   * crede sia il record invece che sul record.
   * Serve a spegnere "Esegui simulazione" su una configurazione rotta. Il builder tiene già spento
   * il proprio pulsante, ma quel cancello vale per chi passa dal pulsante: la pagina è una rotta, e
   * chi la digita o la riapre da un segnalibro lo scavalca.
   * Sempre 200, lista vuota = valida. I messaggi sono già nella lingua dell'utente, come in
   * `validate`: a tradurli è il server, che è l'unico ad avere le chiavi.
   */
  getValidation: (automationDefinitionId: number): Promise<AutomationViolation[]> =>
    httpClient
      .get<Page<AutomationViolation>>(`${SINGLE}/${automationDefinitionId}/validation`)
      .then((r) => r.data.items),

  /**
   * Dry-run dell'automazione sugli ultimi `limit` record (POST /automation/{id}/simulation): quali
   * verrebbero intercettati, senza eseguire azioni né persistere nulla.
   * POST pur essendo di sola lettura: il calcolo scandisce i record del sito e un GET sarebbe
   * cacheabile, cioè il pulsante "esegui" potrebbe non rieseguire. Nessun body, come le transizioni
   * qui sotto: `limit` viaggia in query string.
   * Il tetto sul valore lo applica il SERVER (riconduce, non rifiuta): il client manda quello che
   * l'utente ha scritto e non duplica una soglia che non gli appartiene.
   * Il DTO porta oggi il solo `candidateRecords`: crescerà di campi man mano che il motore saprà
   * calcolarli, e sono aggiunte compatibili — nessun campo cambia significato.
   */
  simulate: (automationDefinitionId: number, limit: number): Promise<AutomationSimulation> =>
    httpClient
      .post<AutomationSimulation>(`${SINGLE}/${automationDefinitionId}/simulation`, undefined, {
        params: { limit },
      })
      .then((r) => r.data),

  // ─── Transizioni di ciclo di vita (POST sub-resource, no body) ───
  publish: (automationDefinitionId: number): Promise<Automation> =>
    httpClient
      .post<AutomationDto>(`${SINGLE}/${automationDefinitionId}/publish`)
      .then((r) => automationFromDto(r.data)),

  suspend: (automationDefinitionId: number): Promise<Automation> =>
    httpClient
      .post<AutomationDto>(`${SINGLE}/${automationDefinitionId}/suspend`)
      .then((r) => automationFromDto(r.data)),

  resume: (automationDefinitionId: number): Promise<Automation> =>
    httpClient
      .post<AutomationDto>(`${SINGLE}/${automationDefinitionId}/resume`)
      .then((r) => automationFromDto(r.data)),

  /**
   * Accende o spegne una singola AZIONE, e risponde con l'AUTOMAZIONE che la contiene: il chiamante
   * sta guardando quella, e riceverla intera gli evita di ricomporre l'elenco per rinfrescare ciò
   * che ha a video.
   * <p>Due verbi e nessun body, come le transizioni qui sopra — il valore sta nel path. Una
   * funzione sola con un booleano, invece di due gemelle: al chiamante interessa lo stato che vuole
   * ottenere, non quale dei due endpoint lo ottiene.
   * <p>Ammesso anche su automazioni ATTIVE: spegnere un'azione non modifica nessuna
   * configurazione, quindi non passa da una nuova versione.
   */
  setActionEnabled: (automationActionId: number, enabled: boolean): Promise<Automation> =>
    httpClient
      .post<AutomationDto>(
        `${SINGLE}/action/${automationActionId}/${enabled ? 'enable' : 'disable'}`,
      )
      .then((r) => automationFromDto(r.data)),

  /**
   * Cambia quanto storico l'automazione deve tenere (PATCH /automation/{id}/log-every-record).
   * Endpoint a sé e non l'update generale: quello riscrive l'intera configurazione e vale sulle
   * sole bozze, mentre questo flag si cambia anche su un'automazione ATTIVA — non tocca cosa
   * l'automazione fa, solo quanto ne resta scritto, e non deve costringere a una nuova versione.
   * Il valore va come query param, non nel corpo: è un booleano solo.
   */
  setLogEveryRecord: (
    automationDefinitionId: number,
    logEveryRecord: boolean,
  ): Promise<Automation> =>
    httpClient
      .patch<AutomationDto>(`${SINGLE}/${automationDefinitionId}/log-every-record`, undefined, {
        params: { value: logEveryRecord },
      })
      .then((r) => automationFromDto(r.data)),

  createVersion: (automationDefinitionId: number): Promise<Automation> =>
    httpClient
      .post<AutomationDto>(`${SINGLE}/${automationDefinitionId}/new-version`)
      .then((r) => automationFromDto(r.data)),

  /**
   * Ripristina i template predefiniti del sito: 204, nessun corpo in risposta.
   *
   * DISTRUTTIVA: di ogni predefinito il server elimina l'INTERA famiglia di versioni, comprese
   * quelle derivate per personalizzarlo. Chi la chiama deve averlo fatto confermare.
   */
  restoreTemplates: (groupId: number): Promise<void> =>
    httpClient.post<void>(`${COLLECTION}/templates/restore/${groupId}`).then(() => undefined),
};
